import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ScheduleItem,
  type ScheduleLink,
  getStatusColor,
  getPhaseColor,
  getDelayStatusColor,
  getDelayStatusLabel,
  getBarColor,
  formatDateLabel,
  formatDateFull,
} from '@/lib/scheduleUtils';
import type { ScheduleChecklistProgress } from '@/hooks/useScheduleChecklistProgress';
import type { LinkedChecklistItem } from '@/lib/scheduleChecklistProgress';
import LinkTypeBadge from './LinkTypeBadge';

interface Props {
  items: ScheduleItem[];
  links: ScheduleLink[];
  canEdit: boolean;
  criticalItemIds: Set<string>;
  computedRisk: Map<string, { delay_status: ScheduleItem['delay_status']; schedule_risk_level: ScheduleItem['schedule_risk_level']; delayDays: number }>;
  checklistProgressMap: Map<string, ScheduleChecklistProgress>;
  linkedChecklists?: Map<string, LinkedChecklistItem[]>;
  onEdit: (item: ScheduleItem) => void;
  onDelete: (id: string) => void;
  onAddLink: (item: ScheduleItem) => void;
  onRemoveLink: (linkId: string) => Promise<void>;
  onToggleComplete: (item: ScheduleItem, checked: boolean) => void;
  onProgressChange: (id: string, progress: number) => void;
  onOpenCompletionBlock: (item: ScheduleItem) => void;
  onReload?: () => void;
  onSendInstruction?: (item: ScheduleItem) => void;
  viewerRole?: 'management' | 'subcontractor' | 'client';
  // Bulk selection
  selectedItemIds?: Set<string>;
  onToggleSelectItem?: (itemId: string) => void;
  canBulkSelect?: boolean;
}

export default function ScheduleListView({
  items,
  links,
  canEdit,
  criticalItemIds,
  computedRisk,
  checklistProgressMap,
  linkedChecklists,
  onEdit,
  onDelete,
  onAddLink,
  onRemoveLink,
  onToggleComplete,
  onProgressChange,
  onOpenCompletionBlock,
  onReload,
  onSendInstruction,
  viewerRole = 'management',
  selectedItemIds,
  onToggleSelectItem,
  canBulkSelect = false,
}: Props) {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-[#374151] font-semibold text-sm">{t('schedule.noItemsMatchCurrentFilter')}</p>
        {onReload && (
          <button onClick={onReload} className="mt-2 text-[#E8621A] text-xs font-semibold cursor-pointer hover:underline">
            {t('schedule.retry')}
          </button>
        )}
      </div>
    );
  }

  /**
   * UNIFIED completion check — matches isScheduleItemFinalComplete() logic.
   * An item is complete if ANY of: is_complete=true, progress>=100, actual_end_date set,
   * or all linked checklist items approved.
   */
  function isScheduleItemCompleteUnified(item: ScheduleItem, cp?: ScheduleChecklistProgress): boolean {
    if (item.is_complete) return true;
    if (item.progress >= 100) return true;
    if (item.actual_end_date != null && item.actual_end_date !== '') return true;
    if (cp?.hasLinkedItems && cp?.allRequiredComplete) return true;
    return false;
  }

  /**
   * Determine a user-friendly reason why this schedule item is not Final 100%.
   * CRITICAL: "No checklist mapped" is a MAPPING status, not a completion status.
   * If an item is manually complete (is_complete=true or progress>=100), it is COMPLETE.
   * We only show a reason badge for items that are NOT complete.
   */
  function getScheduleItemReason(item: ScheduleItem, cp?: ScheduleChecklistProgress): { label: string; color: string; bg: string } | null {
    if (isScheduleItemCompleteUnified(item, cp)) return null;

    const hasLinked = cp?.hasLinkedItems ?? false;
    if (!hasLinked) {
      return { label: t('listView.notMappedReason'), color: '#F59E0B', bg: '#FFFBEB' };
    }
    if (cp && !cp.allRequiredComplete) {
      if ((cp.missingManagement ?? 0) > 0 && (cp.missingSubcontractor ?? 0) > 0) {
        return { label: `${cp.missingManagement} ${t('listView.mgmtPending')} + ${cp.missingSubcontractor} ${t('listView.subPending')}`, color: '#E8621A', bg: '#FEF0E7' };
      }
      if ((cp.missingSubcontractor ?? 0) > 0) {
        return { label: `${cp.missingSubcontractor} ${t('listView.subPending')}`, color: '#F59E0B', bg: '#FFFBEB' };
      }
      if ((cp.missingManagement ?? 0) > 0) {
        return { label: `${cp.missingManagement} ${t('listView.mgmtPending')}`, color: '#E8621A', bg: '#FEF0E7' };
      }
      return { label: t('listView.checklistIncomplete'), color: '#E8621A', bg: '#FEF0E7' };
    }
    if (item.progress === 0) {
      return { label: t('listView.pendingFinalReason'), color: '#EF4444', bg: '#FEF2F2' };
    }
    return { label: t('listView.inProgress', { pct: item.progress }), color: '#F59E0B', bg: '#FFFBEB' };
  }

  return (
    <div className="space-y-2">
      {/* Summary helper text at top of list */}
      {(() => {
        // CRITICAL: Use unified completion logic.
        const pending = items.filter((i) => !isScheduleItemCompleteUnified(i, checklistProgressMap.get(i.id))).length;
        const noMapping = items.filter((i) => {
          const cp = checklistProgressMap.get(i.id);
          return !cp?.hasLinkedItems;
        });
        const noMappingIncomplete = noMapping.filter((i) => !isScheduleItemCompleteUnified(i, checklistProgressMap.get(i.id))).length;
        const noMappingComplete = noMapping.filter((i) => isScheduleItemCompleteUnified(i, checklistProgressMap.get(i.id))).length;
        const incompleteCl = items.filter((i) => {
          const cp = checklistProgressMap.get(i.id);
          return cp?.hasLinkedItems && !cp?.allRequiredComplete && !isScheduleItemCompleteUnified(i, checklistProgressMap.get(i.id));
        }).length;
        if (pending === 0) {
          return (
            <div className="bg-white rounded-xl border border-gray-100 p-3 mb-2">
              <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#6B7280]">
                <i className="ri-checkbox-circle-line text-[#059669]"></i>
                <span className="font-bold text-[#059669]">{t('listView.allComplete', { n: items.length })}</span>
                {noMappingComplete > 0 && (
                  <>
                    <span className="text-[#D1D5DB]">·</span>
                    <span className="text-[#059669]">{t('listView.notMappedComplete', { n: noMappingComplete })}</span>
                  </>
                )}
              </div>
            </div>
          );
        }
        return (
          <div className="bg-white rounded-xl border border-gray-100 p-3 mb-2">
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#6B7280]">
              <span className="font-bold text-[#EF4444]">{pending}</span>
              <span className="text-[#EF4444] font-medium">{t('listView.pendingFinal')}</span>
              {noMappingIncomplete > 0 && (
                <>
                  <span className="text-[#D1D5DB]">·</span>
                  <span className="font-bold text-[#F59E0B]">{noMappingIncomplete}</span>
                  <span>{t('listView.noMappingIncomplete')}</span>
                </>
              )}
              {incompleteCl > 0 && (
                <>
                  <span className="text-[#D1D5DB]">·</span>
                  <span className="font-bold text-[#E8621A]">{incompleteCl}</span>
                  <span>{t('listView.incompleteChecklist')}</span>
                </>
              )}
              {noMappingComplete > 0 && (
                <>
                  <span className="text-[#D1D5DB]">·</span>
                  <span className="text-[#059669]">{t('listView.mappedButComplete', { n: noMappingComplete })}</span>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {items.map((item) => {
        const isExpanded = expandedId === item.id;
        const status = getStatusColor(item.progress, item.is_complete);
        const phaseColor = getPhaseColor(item.phase);
        const itemLinks = links.filter(
          (l) => l.predecessor_item_id === item.id || l.successor_item_id === item.id
        );
        const isCritical = criticalItemIds.has(item.id);
        const risk = computedRisk.get(item.id);
        const rowHighlight = isCritical && risk?.delay_status === 'delayed' ? 'bg-[#FEF2F2]' : '';

        const cp = checklistProgressMap.get(item.id);
        const hasLinked = cp?.hasLinkedItems ?? false;
        const checklistPct = cp?.progressPct ?? 0;
        const checklistBlocked = hasLinked && !cp?.allRequiredComplete;
        const progressSource: 'checklist' | 'manual' = hasLinked ? 'checklist' : 'manual';
        const displayPct = hasLinked ? checklistPct : item.progress;

        const reasonBadge = getScheduleItemReason(item, cp);
        const isSelected = canBulkSelect && selectedItemIds?.has(item.id);

        // Completion control logic — UNIFIED: same as isScheduleItemFinalComplete
        const isComplete = isScheduleItemCompleteUnified(item, cp);
        const canToggleComplete = canEdit;

        // Visual label for pending items that block Project Progress
        const isBlockingProgress = !isComplete;

        return (
          <div key={item.id} className={`bg-white rounded-2xl border ${isSelected ? 'border-[#E8621A] ring-1 ring-[#E8621A]/20' : 'border-gray-100'} overflow-hidden ${rowHighlight}`}>
            <button
              onClick={() => setExpandedId(isExpanded ? null : item.id)}
              className="w-full text-left px-4 py-3.5 flex items-center gap-3 cursor-pointer"
            >
              {/* --- Row area: bulk select + completion --- */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Bulk selection checkbox (small, gray, separate) */}
                {canBulkSelect && onToggleSelectItem && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelectItem(item.id);
                    }}
                    className="w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors"
                    style={{
                      borderColor: isSelected ? '#E8621A' : '#D1D5DB',
                      backgroundColor: isSelected ? '#E8621A' : 'transparent',
                    }}
                  >
                    {isSelected && <i className="ri-check-line text-white text-[10px]"></i>}
                  </div>
                )}

                {/* Completion toggle: clear labeled pill button */}
                {canToggleComplete ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isComplete && checklistBlocked && viewerRole !== 'management') {
                        onOpenCompletionBlock(item);
                        return;
                      }
                      onToggleComplete(item, !isComplete);
                    }}
                    className={`
                      flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold cursor-pointer whitespace-nowrap transition-all
                      ${isComplete
                        ? 'bg-[#F0FDF4] text-[#059669] border border-[#BBF7D0]'
                        : 'bg-[#FEF0E7] text-[#E8621A] border border-[#FDBA74] hover:bg-[#FDBA74]/20'
                      }
                    `}
                  >
                    {isComplete ? (
                      <><i className="ri-checkbox-circle-line text-[10px]"></i>{t('listView.completed')}</>
                    ) : (
                      <><i className="ri-checkbox-blank-circle-line text-[10px]"></i>{t('listView.markComplete')}</>
                    )}
                  </button>
                ) : (
                  <span
                    className={`
                      flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap
                      ${isComplete
                        ? 'bg-[#F0FDF4] text-[#059669] border border-[#BBF7D0]'
                        : 'bg-[#F9FAFB] text-[#9CA3AF] border border-gray-200'
                      }
                    `}
                  >
                    {isComplete ? (
                      <><i className="ri-checkbox-circle-line text-[10px]"></i>{t('listView.completed')}</>
                    ) : (
                      <><i className="ri-checkbox-blank-circle-line text-[10px]"></i>{t('listView.incomplete')}</>
                    )}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {isCritical && (
                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-[#EF4444]" title="Critical path" />
                  )}
                  <span className="text-sm font-semibold text-[#1A1A1A] truncate">{item.title}</span>
                  {item.phase && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100"
                      style={{ color: phaseColor }}
                    >
                      {item.phase}
                    </span>
                  )}
                  {(item.main_stage || item.construction_sub_stage) && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#F9FAFB] text-[#9CA3AF] flex items-center gap-1"
                    >
                      <i className="ri-folder-line text-[8px]"></i>
                      {item.main_stage}
                      {item.construction_sub_stage && (
                        <span className="text-[#E8621A]">· {item.construction_sub_stage}</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className="text-[#9CA3AF] text-xs">{item.duration_days} days</span>
                  <span className="text-[#D1D5DB] text-xs">·</span>
                  <span className="text-[#9CA3AF] text-xs">
                    {formatDateLabel(item.start_date)} → {formatDateLabel(item.end_date)}
                  </span>
                  {item.assigned_role && (
                    <>
                      <span className="text-[#D1D5DB] text-xs">·</span>
                      <span className="text-[#9CA3AF] text-xs">{item.assigned_role}</span>
                    </>
                  )}
                  {/* Reason badge inline — always show when not complete */}
                  {reasonBadge && (
                    <>
                      <span className="text-[#D1D5DB] text-xs">·</span>
                      <span
                        className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{ backgroundColor: reasonBadge.bg, color: reasonBadge.color }}
                      >
                        {reasonBadge.label}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Blocking progress label — prominent for incomplete items */}
                {!isComplete && (
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-[#FEF2F2] text-[#EF4444] border border-[#FECACA] whitespace-nowrap animate-pulse">
                    <i className="ri-error-warning-line mr-0.5"></i>
                    {t('listView.blockingProgress')}
                  </span>
                )}

                {/* Checklist progress with explicit three-tier labels */}
                {hasLinked ? (
                  <div className="flex flex-col items-end gap-0.5">
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: checklistBlocked ? '#FEF0E7' : '#F0FDF4', color: checklistBlocked ? '#92400E' : '#059669' }}
                    >
                      Final {cp?.completedRequiredCount}/{cp?.requiredCount}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] font-bold text-[#9CA3AF]">Mgmt {cp?.managementCheckedCount}/{cp ? cp.managementCheckedCount + cp.missingManagement : 0}</span>
                      {cp && (cp.subcontractorCheckedCount + cp.missingSubcontractor) > 0 && (
                        <span className="text-[9px] font-bold text-[#0891B2]">Sub {cp.subcontractorCheckedCount}/{cp.subcontractorCheckedCount + cp.missingSubcontractor}</span>
                      )}
                    </div>
                    <span className="text-[9px] font-bold text-[#9CA3AF]">{checklistPct}%</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FFFBEB] text-[#F59E0B]">
                      {t('listView.notMapped')}
                    </span>
                    <span className="text-[9px] text-[#9CA3AF]">
                      {isComplete ? t('listView.manualComplete') : item.progress > 0 ? `Manual ${item.progress}%` : t('listView.noChecklist')}
                    </span>
                  </div>
                )}
                {checklistBlocked && (
                  <span className="text-[10px] font-bold text-[#F59E0B] bg-[#FFFBEB] px-1.5 py-0.5 rounded-full flex items-center gap-0.5 whitespace-nowrap">
                    <i className="ri-lock-line text-[8px]"></i>
                    {cp?.missingSubcontractor > 0 && cp?.missingManagement > 0
                      ? `${cp.missingManagement} mgmt + ${cp.missingSubcontractor} sub pending`
                      : cp?.missingSubcontractor > 0
                        ? `${cp.missingSubcontractor} sub pending`
                        : cp?.missingManagement > 0
                          ? `${cp.missingManagement} mgmt pending`
                          : 'Locked'}
                  </span>
                )}
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: status.bg, color: status.text }}
                >
                  {displayPct}%
                </span>
                {progressSource === 'checklist' && (
                  <span className="text-[9px] text-[#0891B2] bg-[#F0F9FF] px-1 py-0.5 rounded-full font-bold">CL</span>
                )}
                {progressSource === 'manual' && (
                  <span className="text-[9px] text-[#9CA3AF] bg-gray-100 px-1 py-0.5 rounded-full font-bold">M</span>
                )}
                {risk && risk.delay_status !== 'on_track' && risk.delay_status !== 'completed_on_time' && (
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: getDelayStatusColor(risk.delay_status).bg,
                      color: getDelayStatusColor(risk.delay_status).text,
                    }}
                  >
                    {getDelayStatusLabel(risk.delay_status)}
                  </span>
                )}
                {itemLinks.length > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280]">
                    {itemLinks.length} link{itemLinks.length > 1 ? 's' : ''}
                  </span>
                )}
                {isCritical && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FEF2F2] text-[#EF4444]">
                    Critical
                  </span>
                )}
                {item.baseline_end_date && item.end_date && (() => {
                  const cur = new Date(item.end_date).getTime();
                  const base = new Date(item.baseline_end_date).getTime();
                  if (cur > base) {
                    const diff = Math.ceil((cur - base) / (1000 * 60 * 60 * 24));
                    return (
                      <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-[#FEF0E7] text-[#92400E]">
                        +{diff}d
                      </span>
                    );
                  }
                  return null;
                })()}
                <i
                  className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#D1D5DB] text-lg`}
                ></i>
              </div>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="pt-3 space-y-3">
                  {/* Status reason detail */}
                  {reasonBadge && (
                    <div className="rounded-xl p-3 border" style={{ backgroundColor: reasonBadge.bg, borderColor: reasonBadge.color + '30' }}>
                      <div className="flex items-start gap-2">
                        <i className="ri-error-warning-line text-sm flex-shrink-0 mt-0.5" style={{ color: reasonBadge.color }}></i>
                        <div>
                          <p className="text-xs font-semibold" style={{ color: reasonBadge.color }}>
                            {reasonBadge.label}
                          </p>
                          {!hasLinked && (
                            <p className="text-[11px] text-[#6B7280] mt-0.5 leading-relaxed">
                              {t('listView.noChecklistLinked')}
                            </p>
                          )}
                          {hasLinked && !cp?.allRequiredComplete && (
                            <p className="text-[11px] text-[#6B7280] mt-0.5 leading-relaxed">
                              {t('listView.linkedChecklistIncomplete')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Progress */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[#555555] text-xs font-semibold">{t('listView.progressLabel')}</span>
                      <div className="flex items-center gap-2">
                        {hasLinked ? (
                          <span className="text-[10px] text-[#9CA3AF]">
                            {cp?.completedRequiredCount}/{cp?.requiredCount} {t('listView.checklistItems')}
                          </span>
                        ) : null}
                        <span className="text-xs font-bold" style={{ color: status.text }}>
                          {displayPct}%
                        </span>
                      </div>
                    </div>
                    {canEdit ? (
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={displayPct}
                        onChange={(e) => onProgressChange(item.id, Number(e.target.value))}
                        disabled={hasLinked}
                        className="w-full accent-[#E8621A]"
                      />
                    ) : (
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${displayPct}%`, backgroundColor: getBarColor(item) }}
                        />
                      </div>
                    )}
                    {hasLinked && (
                      <p className="text-[10px] text-[#9CA3AF] mt-1">
                        {t('listView.progressDerivedFromChecklist')}
                      </p>
                    )}
                    {!hasLinked && (
                      <p className="text-[10px] text-[#9CA3AF] mt-1">
                        {t('listView.noChecklistLinkedManual')}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="bg-[#F9FAFB] rounded-xl p-3">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('listView.start')}</p>
                      <p className="text-[#374151] text-xs font-medium">{formatDateFull(item.start_date)}</p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-xl p-3">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('listView.end')}</p>
                      <p className="text-[#374151] text-xs font-medium">{formatDateFull(item.end_date)}</p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-xl p-3">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('listView.duration')}</p>
                      <p className="text-[#374151] text-xs font-medium">{item.duration_days} {t('listView.workingDays')}</p>
                    </div>
                    <div className="bg-[#F9FAFB] rounded-xl p-3">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('listView.assigned')}</p>
                      <p className="text-[#374151] text-xs font-medium">{item.assigned_role || item.assigned_to || '—'}</p>
                    </div>
                  </div>

                  {/* Checklist detail */}
                  {cp && cp.hasLinkedItems && (
                    <div className={`rounded-xl p-3 border ${cp.allRequiredComplete ? 'bg-[#F0FDF4] border-[#BBF7D0]' : 'bg-[#FEF0E7] border-[#FDBA74]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-[#374151]">{t('listView.checklistCompletion')}</span>
                        <span className={`text-xs font-bold ${cp.allRequiredComplete ? 'text-[#059669]' : 'text-[#92400E]'}`}>
                          {cp.completedRequiredCount} / {cp.requiredCount}
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${cp.progressPct}%`, backgroundColor: cp.allRequiredComplete ? '#059669' : '#E8621A' }}
                        />
                      </div>
                      <div className="flex items-center gap-3 mt-2 flex-wrap text-[10px]">
                        <span className="text-[#9CA3AF]">{t('listView.mgmt')}: <span className="font-semibold text-[#374151]">{cp.managementCheckedCount}</span></span>
                        <span className="text-[#9CA3AF]">{t('listView.sub')}: <span className="font-semibold text-[#374151]">{cp.subcontractorCheckedCount}</span></span>
                        {cp.missingManagement > 0 && (
                          <span className="text-[#F59E0B] font-semibold">{cp.missingManagement} {t('listView.mgmtPending')}</span>
                        )}
                        {cp.missingSubcontractor > 0 && (
                          <span className="text-[#F59E0B] font-semibold">{cp.missingSubcontractor} {t('listView.subPending')}</span>
                        )}
                      </div>
                      {/* Individual checklist items */}
                      {linkedChecklists && linkedChecklists.has(item.id) && (
                        <div className="mt-3 pt-3 border-t border-gray-200/50 space-y-2">
                          <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1.5">{t('listView.linkedChecklistItems')}</p>
                          {(linkedChecklists.get(item.id) || []).map((li) => {
                            const needsMgmt = li.requires_management_check !== false;
                            const needsSub = li.requires_subcontractor_check === true;
                            const isComplete = li.is_completed;
                            return (
                              <div key={li.id} className={`flex items-start gap-2 rounded-lg px-2.5 py-2 border ${isComplete ? 'border-[#059669]/20 bg-[#F0FDF4]/50' : 'border-gray-100 bg-white'}`}>
                                <div className="flex flex-col gap-0.5 flex-shrink-0 mt-0.5">
                                  {needsMgmt && (
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${li.management_checked ? 'bg-[#059669] border-[#059669]' : 'bg-white border-gray-200'}`}>
                                      {li.management_checked ? <i className="ri-check-line text-white text-[8px]"></i> : <span className="text-[7px] font-bold text-[#9CA3AF]">M</span>}
                                    </div>
                                  )}
                                  {needsSub && (
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${li.subcontractor_checked ? 'bg-[#0891B2] border-[#0891B2]' : 'bg-white border-gray-200'}`}>
                                      {li.subcontractor_checked ? <i className="ri-check-line text-white text-[8px]"></i> : <span className="text-[7px] font-bold text-[#9CA3AF]">S</span>}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs font-medium leading-snug ${isComplete ? 'text-[#9CA3AF] line-through' : 'text-[#374151]'}`}>
                                    {li.title}
                                  </p>
                                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                    {needsMgmt && (
                                      <span className={`text-[9px] font-bold px-1 py-0.5 rounded-full ${li.management_checked ? 'bg-[#F0FDF4] text-[#059669]' : 'bg-[#FEF0E7] text-[#E8621A]'}`}>
                                        {t('listView.mgmt')}: {li.management_checked ? t('progress.done') : t('progress.pending')}
                                      </span>
                                    )}
                                    {needsSub && (
                                      <span className={`text-[9px] font-bold px-1 py-0.5 rounded-full ${li.subcontractor_checked ? 'bg-[#ECFEFF] text-[#0891B2]' : 'bg-[#FFFBEB] text-[#F59E0B]'}`}>
                                        {t('listView.sub')}: {li.subcontractor_checked ? t('progress.done') : t('progress.pending')}
                                      </span>
                                    )}
                                    {needsSub && li.management_checked && !li.subcontractor_checked && (
                                      <span className="text-[9px] font-bold bg-[#FFFBEB] text-[#F59E0B] px-1 py-0.5 rounded-full">{t('listView.waitingForSub')}</span>
                                    )}
                                    {isComplete && (
                                      <span className="text-[9px] font-bold bg-[#F0FDF4] text-[#059669] px-1 py-0.5 rounded-full">{t('listView.finalComplete')}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {(item.baseline_start_date || item.baseline_end_date || isCritical || risk) && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {item.baseline_start_date && (
                        <div className="bg-[#F5F3FF] rounded-xl p-3">
                          <p className="text-[#7C3AED] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('listView.baselineStart')}</p>
                          <p className="text-[#374151] text-xs font-medium">{formatDateFull(item.baseline_start_date)}</p>
                        </div>
                      )}
                      {item.baseline_end_date && (
                        <div className="bg-[#F5F3FF] rounded-xl p-3">
                          <p className="text-[#7C3AED] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('listView.baselineEnd')}</p>
                          <p className="text-[#374151] text-xs font-medium">{formatDateFull(item.baseline_end_date)}</p>
                        </div>
                      )}
                      {isCritical && (
                        <div className="bg-[#FEF2F2] rounded-xl p-3">
                          <p className="text-[#EF4444] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('listView.criticalPath')}</p>
                          <p className="text-[#EF4444] text-xs font-bold">{t('listView.criticalPathYes')}</p>
                        </div>
                      )}
                      {risk && risk.delayDays > 0 && (
                        <div className="bg-[#FEF0E7] rounded-xl p-3">
                          <p className="text-[#92400E] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('listView.delay')}</p>
                          <p className="text-[#92400E] text-xs font-bold">{t('listView.delayDays', { n: risk.delayDays, plural: risk.delayDays > 1 ? 's' : '' })}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {item.notes && (
                    <p className="text-[#6B7280] text-sm leading-relaxed">{item.notes}</p>
                  )}

                  {itemLinks.length > 0 && (
                    <div className="bg-[#F9FAFB] rounded-xl p-3">
                      <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-2">{t('listView.dependencies')}</p>
                      <div className="space-y-1.5">
                        {itemLinks.map((l) => {
                          const isPre = l.predecessor_item_id === item.id;
                          const otherId = isPre ? l.successor_item_id : l.predecessor_item_id;
                          const other = items.find((i) => i.id === otherId);
                          return (
                            <div key={l.id} className="flex items-center gap-2 text-xs">
                              <LinkTypeBadge type={l.link_type} />
                              <span className="text-[#374151] font-medium">
                                {isPre ? '→' : '←'} {other?.title || 'Unknown'}
                              </span>
                              {l.lag_days > 0 && <span className="text-[#9CA3AF]">+{l.lag_days}d lag</span>}
                              {canEdit && (
                                <button
                                  onClick={() => onRemoveLink(l.id)}
                                  className="text-[#EF4444] text-[10px] font-bold ml-auto cursor-pointer"
                                >
                                  {t('listView.remove')}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {canEdit && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => onEdit(item)}
                        className="flex items-center gap-1.5 text-[#E8621A] text-xs font-semibold cursor-pointer"
                      >
                        <i className="ri-pencil-line text-sm"></i>{t('listView.editItem')}
                      </button>
                      <button
                        onClick={() => onAddLink(item)}
                        className="flex items-center gap-1.5 text-[#7C3AED] text-xs font-semibold cursor-pointer"
                      >
                        <i className="ri-link-m text-sm"></i>{t('listView.addLink')}
                      </button>
                      <button
                        onClick={() => onSendInstruction?.(item)}
                        className="flex items-center gap-1.5 text-[#059669] text-xs font-semibold cursor-pointer"
                      >
                        <i className="ri-send-plane-line text-sm"></i>{t('listView.sendInstruction')}
                      </button>
                      <button
                        onClick={() => onDelete(item.id)}
                        className="flex items-center gap-1.5 text-[#EF4444] text-xs font-semibold cursor-pointer ml-auto"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>{t('listView.deleteItem')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}